import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAdminAuth } from './AdminAuthContext';
import './AdminPage.css';

interface Submission {
  id: string;
  created_at: string;
  company_name: string;
  business_email: string | null;
  privacy_consent: number;
}

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        setIsLoading(true);
        setError('');

        const response = await fetch('/api/admin/submissions', {
          method: 'GET',
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          setSubmissions(data.submissions || []);
        } else if (response.status === 401) {
          // Session expired, redirect to login
          navigate('/admin');
        } else {
          throw new Error(`Failed to load submissions: ${response.status}`);
        }
      } catch (err) {
        console.error('Error fetching submissions:', err);
        setError('Failed to load submissions. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubmissions();
  }, [navigate]);

  const { logout } = useAdminAuth();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-dashboard">
      <div className="container">
        <div className="admin-header">
          <div>
            <h1>China Readiness Check</h1>
            <h2>Submissions</h2>
          </div>
          <button
            onClick={() => logout()}
            className="btn btn-outline logout-btn"
          >
            Logout
          </button>
        </div>

        {error && <div className="form-error">{error}</div>}

        {isLoading ? (
          <div className="loading">Loading submissions...</div>
        ) : submissions.length === 0 ? (
          <div className="empty-state">
            <h3>No submissions yet</h3>
            <p>When companies complete the China Readiness Check, their submissions will appear here.</p>
          </div>
        ) : (
          <table className="submissions-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Company</th>
                <th>Business email</th>
                <th>Consent</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((submission) => (
                <tr key={submission.id}>
                  <td>{formatDate(submission.created_at)}</td>
                  <td>{submission.company_name}</td>
                  <td>{submission.business_email || '-'}</td>
                  <td>{submission.privacy_consent ? 'Yes' : 'No'}</td>
                  <td>
                    <div className="submission-actions">
                      <Link
                        to={`/admin/submissions/${submission.id}`}
                        className="view-btn"
                      >
                        View
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
